"use client"

import { useEffect, useRef } from "react"
import { X } from "lucide-react"
import { ButtonFluxi } from "../common/Buttons/ButtonFluxi/ButtonFluxi"
import { Loader2 } from "lucide-react"

interface BaseModalProps {
  isOpen: boolean
  onClose: () => void
  title: string
  description: string
  imageSrc?: string
  imageAlt?: string
  buttonOne?: {
    text: string
    onClick: () => void
    disabled?: boolean
    loading?: boolean
    variant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link"
    className?: string
  }
  buttonTwo?: {
    text: string
    onClick: () => void
    disabled?: boolean
    loading?: boolean
    variant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link"
    className?: string
  }
  showCloseButton?: boolean
}

export function BaseModal({
  isOpen,
  onClose,
  title,
  description,
  imageSrc,
  imageAlt = "Modal image",
  buttonOne,
  buttonTwo,
  showCloseButton = true
}: BaseModalProps) {
  const modalRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside)
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div 
        ref={modalRef}
        className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 relative"
      >
        {/* Close button */}
        {showCloseButton && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-10 p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        )}

        <div className="p-6">
          {/* Image/GIF/Video */}
          {imageSrc && (
            <div className="flex justify-center">
              <div className="relative">
                {imageSrc.endsWith('.webm') || imageSrc.endsWith('.mp4') ? (
                  <video
                    src={imageSrc}
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="max-w-full h-auto max-h-48 object-contain"
                  />
                ) : (
                  <img
                    src={imageSrc}
                    alt={imageAlt}
                    className="max-w-full h-auto max-h-48 object-contain"
                  />
                )}
              </div>
            </div>
          )}

          {/* Title */}
          <h2 className="text-xl font-bold text-center text-gray-900 mb-4">
            {title}
          </h2>

          {/* Description */}
          <p className="text-gray-600 text-center mb-8 leading-relaxed">
            {description}
          </p>

          {/* Buttons */}
          <div className="flex gap-3">
            {buttonOne && (
              <ButtonFluxi 
                text={buttonOne.loading ? "" : buttonOne.text}
                onClick={buttonOne.onClick} 
                disabled={buttonOne.disabled} 
                isEffect={false} 
                fullWidth={true}
              >
                {buttonOne.loading && <Loader2 className="w-4 h-4 animate-spin" />}
              </ButtonFluxi>
            )}
            
            {buttonTwo && (
              <ButtonFluxi 
                text={buttonTwo.loading ? "" : buttonTwo.text}
                onClick={buttonTwo.onClick} 
                disabled={buttonTwo.disabled} 
                isEffect={false} 
                fullWidth={true} 
                gradient_from="#ffffff" 
                gradient_to="#ffffff" 
                border_color="#595EEE" 
                color="#595EEE" 
              >
                {buttonTwo.loading && <Loader2 className="w-4 h-4 animate-spin" />}
              </ButtonFluxi>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}